import { useEffect, useMemo, useState } from 'react'

import type { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'

import * as Icons from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import AddUsers from 'components/UI/Modal/AddUsers/AddUsers'
import AddUserToGroups from 'components/UI/Modal/AddUserToGroups/AddUserToGroups'
import UserDetails from 'components/UI/Modal/UserDetails/UserDetails'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { useModalContext } from 'context/modalContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { someTimeAgoFans } from 'lib/dayjs'
import {
  addFanToGroups,
  editFansGroup,
  getUsersFromFansGroup
} from 'services/endpoints/fanGroup'

import {
  getFansCustomGroupFilterOptions,
  getFansCustomGroupSortOptions,
  getFansCustomGroupSortOrderOptions,
  initialFilterSelect
} from './fansCustomGroupOptions'

type ApiErrorShape = {
  code?: number
  message?: string
}

type GroupMeta = {
  id: string
  name: string
  slug: string
  default: number | boolean
}

type GroupUsersResponse = {
  group: GroupMeta | null
  users: any[]
}

const isAxiosError = (err: unknown): err is AxiosError<any> => {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as any).isAxiosError === true
  )
}

export const FansCustomGroup = ({
  group: passedGroup
}: {
  group?: GroupMeta | null
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const normalizedSearch = searchTerm.trim()
  const normalizedDebouncedSearch = debouncedSearch.trim()

  const searchIsSettled =
    normalizedSearch === normalizedDebouncedSearch

  const [dependecyArray, setDependecyArray] = useState<string[]>([])

  const { t } = useTranslation()
  const navigate = useNavigate()
  const modalData = useModalContext()
  const queryClient = useQueryClient()

  const [filterSelectDefaults, setFilterSelectDefaults] =
    useState<Record<string, string | number>>(initialFilterSelect)

  const [filterOption, setFilterOption] = useState('')
  const [sortOption, setSortOption] = useState('')

  const [sortAscOrDesc, setSortAscOrDesc] =
    useState<'asc' | 'desc' | ''>('desc')

  const { slug } = useParams()

  const groupSlug = slug ?? null

  const filterValue =
    filterOption === ''
      ? null
      : (filterSelectDefaults[filterOption] as any)

  const normalizeFanForGroupList = (u: any) => {
    return {
      ...u,
      id: u.id,
      display_name: u.display_name ?? u.name ?? '',
      username: (u.username ?? '').replace(/^@/, ''),
      avatar: u.avatar?.url ? u.avatar : { url: u.avatar ?? '' },
      statistics: u.statistics ?? {
        name: u.display_name ?? u.name ?? '',
        total_spent: u.total_spent ?? 0,
        tipped: u.tipped ?? 0,
        last_activity: u.last_activity ?? null,
        join_date: u.join_date ?? null,
        total_months_subscribed:
          u.total_months_subscribed ?? 0,
        upsell_conversion_ratio:
          u.upsell_conversion_ratio ?? 0,
        expiring: u.expiring ?? null
      }
    }
  }

  const {
    data: groupPayload,
    isLoading: isLoadingUsers
  } = useQuery<GroupUsersResponse>(
    [
      'fans-group',
      groupSlug,
      filterOption,
      filterValue,
      sortOption,
      sortAscOrDesc,
      normalizedDebouncedSearch
    ],
    async () => {
      const groupIdentifier = passedGroup?.id ?? groupSlug

      return await getUsersFromFansGroup(groupIdentifier, {
        filter_by: filterOption === '' ? null : filterOption,
        filter_value: filterOption === '' ? null : filterSelectDefaults[filterOption],
        sort_by: sortOption === '' ? null : sortOption,
        sort_value: sortAscOrDesc === '' ? null : sortAscOrDesc,
        search: normalizedDebouncedSearch || null
      })
    },
    {
      enabled: !!(passedGroup?.id ?? groupSlug) && searchIsSettled,
      keepPreviousData: true,
      staleTime: 30000,
      refetchOnWindowFocus: false,
      onSuccess: response => {
        setDependecyArray((response.users ?? []).map((u: any) => u.id))
      },
      onError: err => {
        if (isAxiosError(err)) {
          const code = (err.response?.data as ApiErrorShape | undefined)?.code

          if (code === 404) {
            navigate('/404')
            return
          }
        }

        addToast('error', t('anErrorOccuredPleaseTryAgain'))
      }
    }
  )

  const group = passedGroup ?? groupPayload?.group ?? null
  const groupId = group?.id ?? null
  const users = groupPayload?.users ?? []

  const groupTitle = useMemo(() => {
    const raw = (group?.name ?? groupSlug ?? '').toString().trim()

    if (!raw) return t('allActive')

    return raw.toLowerCase() === 'all' ? t('allActive') : raw
  }, [group?.name, groupSlug, t])

  const groupDefault = useMemo(() => {
    if (group) {
      return group.default === 1 || group.default === true
    }

    const slug = String(groupSlug ?? '')
      .toLowerCase()
      .trim()

    return slug === 'all'
  }, [group, groupSlug])

  useEffect(() => {
    if (groupTitle === 'Premium Buyers') {
      setSortOption('total_spent')
      setSortAscOrDesc('desc')
    }
  }, [groupTitle])

  type AddUsersSubmitPayload = {
    ids: string[]
    users: any[]
  }

  const addFansToGroup = useMutation(
    async (payload: AddUsersSubmitPayload) => {
      const resolvedGroupId = groupId

      if (!resolvedGroupId) {
        throw new Error('Missing group id')
      }

      return await editFansGroup(resolvedGroupId, {
        name: groupTitle,
        participants: payload.ids
      })
    },
    {
      onMutate: async (payload: AddUsersSubmitPayload) => {
        const keyPrefix = ['fans-group', groupSlug]

        await queryClient.cancelQueries(keyPrefix)

        const prevEntries =
          queryClient.getQueriesData<GroupUsersResponse>(
            keyPrefix
          )

        queryClient.setQueriesData<GroupUsersResponse>(
          keyPrefix,
          (
            old: GroupUsersResponse | undefined
          ): GroupUsersResponse => {
            if (!old) {
              return {
                group: null,
                users: []
              }
            }

            const existing = old.users ?? []

            const existingIds = new Set(
              existing.map((user: any) => user.id)
            )

            const incoming =
              (payload.users ?? []).map(
                normalizeFanForGroupList
              )

            const merged = [...existing]

            for (const user of incoming) {
              if (!existingIds.has(user.id)) {
                merged.push(user)
                existingIds.add(user.id)
              }
            }

            return {
              ...old,
              users: merged
            }
          }
        )

        setDependecyArray(previous => {
          const updated = new Set(previous)

          for (const fanId of payload.ids) {
            updated.add(fanId)
          }

          return Array.from(updated)
        })

        return { prevEntries }
      },

      onError: (_error, _payload, context) => {
        if (context?.prevEntries) {
          for (const [key, data] of context.prevEntries) {
            queryClient.setQueryData(key, data)
          }
        }

        addToast(
          'error',
          t('anErrorOccuredPleaseTryAgain')
        )

        modalData.clearModal()
      },

      onSuccess: (_response, payload) => {
        const currentGroupId = groupId

        payload.ids.forEach((fanId: string) => {
          queryClient.setQueryData(
            ['fan-groups-membership', fanId],
            (old: any): any => {
              if (!old) return old

              const updateGroups = (
                groups: any[]
              ): any[] =>
                groups.map((groupRow: any) =>
                  groupRow.id === currentGroupId
                    ? {
                        ...groupRow,
                        belongs: true
                      }
                    : groupRow
                )

              if (Array.isArray(old)) {
                return updateGroups(old)
              }

              if (Array.isArray(old.data)) {
                return {
                  ...old,
                  data: updateGroups(old.data)
                }
              }

              if (Array.isArray(old.data?.data)) {
                return {
                  ...old,
                  data: {
                    ...old.data,
                    data: updateGroups(old.data.data)
                  }
                }
              }

              return old
            }
          )
        })

        modalData.clearModal()
      }
    }
  )

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 500)

    return () => clearTimeout(timeout)
  }, [searchTerm])

  
  const addFanToGroupsMutation = useMutation(
    async (mutationInfo: {
      id: string
      groupIds: string[]
    }) =>
      await addFanToGroups(
        mutationInfo.id,
        mutationInfo.groupIds
      ),
    {
      onSuccess: (_resp, params) => {
        const currentGroupId = groupId
        const selectedGroupIds = params.groupIds
        const fanId = params.id

        if (groupSlug && currentGroupId) {
          const fanStillBelongsToCurrentGroup =
            selectedGroupIds.includes(currentGroupId)

          if (!fanStillBelongsToCurrentGroup) {
            queryClient.setQueriesData<GroupUsersResponse>(
              ['fans-group', groupSlug],
              (
                old: GroupUsersResponse | undefined
              ): GroupUsersResponse => {
                if (!old) {
                  return {
                    group: null,
                    users: []
                  }
                }

                return {
                  ...old,
                  users: old.users.filter(
                    cachedUser =>
                      cachedUser?.id !== fanId
                  )
                }
              }
            )

            setDependecyArray(previous =>
              previous.filter(id => id !== fanId)
            )
          }
        }

        queryClient.setQueryData(
          ['fan-groups-membership', fanId],
          (old: any): any => {
            if (!old) return old

            const updateRows = (
              rows: any[]
            ): any[] =>
              rows.map((groupRow: any) => ({
                ...groupRow,
                belongs:
                  selectedGroupIds.includes(
                    groupRow.id
                  )
              }))

            if (Array.isArray(old)) {
              return updateRows(old)
            }

            if (Array.isArray(old.data)) {
              return {
                ...old,
                data: updateRows(old.data)
              }
            }

            if (Array.isArray(old.data?.data)) {
              return {
                ...old,
                data: {
                  ...old.data,
                  data: updateRows(old.data.data)
                }
              }
            }

            return old
          }
        )

        queryClient.invalidateQueries('fans-groups')

        modalData.clearModal()
      },

      onError: () => {
        addToast(
          'error',
          t('anErrorOccuredPleaseTryAgain')
        )

        queryClient.invalidateQueries('fans-groups')
        modalData.clearModal()
      }
    }
  )

  const sortListsGroups = useMemo(
    () => getFansCustomGroupSortOptions(t),
    [t]
  )

  const sortElementsOrder = useMemo(
    () => getFansCustomGroupSortOrderOptions(t),
    [t]
  )

  const filterItemsBy = useMemo(
    () => getFansCustomGroupFilterOptions(t),
    [t]
  )

  const renderSortOptionTags = (user: any) => {
    if (sortOption !== '' && sortOption !== 'name') {
      const monthArray = [
        t('jan'),
        t('feb'),
        t('mar'),
        t('apr'),
        t('may'),
        t('jun'),
        t('jul'),
        t('aug'),
        t('sept'),
        t('oct'),
        t('nov'),
        t('dec')
      ]

      if (
        sortOption === 'expiring' ||
        sortOption === 'join_date'
      ) {
        const tagText =
          sortOption === 'expiring'
            ? t('subRenewDate')
            : t('joinDateOn')

        const raw = user.statistics[sortOption]

        const d = raw
          ? new Date(
              String(raw).replace(/\s+/, 'T')
            )
          : null

        if (!d || Number.isNaN(d.getTime())) {
          return ''
        }

        const day = d.getDate()
        const year = d.getFullYear()
        const month = monthArray[d.getMonth()]
        const currentYear = new Date().getFullYear()

        const formatted =
          year === currentYear
            ? `${month} ${day}`
            : `${month} ${day}, ${year}`

        return (
          <div className='fans-group-sort-option-tag'>
            {tagText} <span>{formatted}</span>
          </div>
        )
      }

      if (sortOption === 'last_activity') {
        const raw = user?.statistics?.last_activity

        if (!raw) return ''

        const out = someTimeAgoFans(raw)

        if (!out) return ''

        return (
          <div className='fans-group-sort-option-tag'>
            {t('lastActivityOn')} <span>{out}</span>
          </div>
        )
      }

      if (sortOption === 'total_spent') {
        return user.statistics.total_spent != null ? (
          <div className='fans-group-sort-option-tag'>
            {t('totalSpent')}{' '}
            <span>
              $
              {Number(
                user.statistics.total_spent
              ).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </span>
          </div>
        ) : (
          ''
        )
      }

      if (
        sortOption === 'total_months_subscribed'
      ) {
        return user.statistics
          .total_months_subscribed ? (
          <div className='fans-group-sort-option-tag'>
            {t('totalMonthsSubscribed')}{' '}
            <span>
              {
                user.statistics
                  .total_months_subscribed
              }
            </span>
          </div>
        ) : (
          ''
        )
      }

      if (
        sortOption === 'upsell_conversion_ratio'
      ) {
        return user.statistics
          .upsell_conversion_ratio ? (
          <div className='fans-group-sort-option-tag'>
            {t('upsellConversionRatio')}{' '}
            <span>
              {
                user.statistics
                  .upsell_conversion_ratio
              }
            </span>
          </div>
        ) : (
          ''
        )
      }
    }

    return ''
  }

  const isLoading = isLoadingUsers

  return (
    <WithHeaderSection
      headerSection={
        <LayoutHeader
          type='search-with-buttons'
          searchValue={searchTerm}
          searchFn={(term: string) =>
            setSearchTerm(term)
          }
          clearFn={() => setSearchTerm('')}
          additionalProps={{
            placeholder: t('searchFans')
          }}
          sortingProps={{
            selectedSort: sortOption,
            selectedOrder: sortAscOrDesc
          }}
          buttons={[
            {
              type: 'sort',
              customClass: 'aaa',
              elements: {
                first_section: sortListsGroups,
                second_section: sortElementsOrder
              },
              sortingProps: {
                selectedSort: sortOption,
                selectedOrder: sortAscOrDesc
              },
              applyFn: (
                val1: string,
                val2: string
              ) => {
                if (val1.trim() === '') {
                  setSortOption('')
                  setSortAscOrDesc('')
                  return
                } else if (val2.trim() === '') {
                  setSortOption(val1)
                  setSortAscOrDesc('asc')
                  return
                }

                setSortOption(val1)

                setSortAscOrDesc(
                  val2 as 'asc' | 'desc'
                )
              }
            },
            {
              type: 'filter',
              elements: {
                first_section: filterItemsBy
              },
              hasResetBtn: true,
              sortingProps: {
                selectedSort: filterOption,
                selectedOrder: '',
                selectProps: filterSelectDefaults
              },
              applyFn: (
                val1: string,
                val2: string,
                selectVal: Record<
                  string,
                  string | number
                >
              ) => {
                setFilterOption(val1)
                setFilterSelectDefaults(selectVal)
              },
              resetFn: (
                setVal: any,
                setVal2: any,
                setSelectVal: any
              ) => {
                setFilterOption('')

                setFilterSelectDefaults(
                  initialFilterSelect
                )

                setVal('')
                setVal2('')

                setSelectVal(
                  initialFilterSelect
                )
              }
            },

            ...(groupDefault
              ? []
              : [
                  {
                    type: 'custom',
                    icon: <Icons.IconPlus />,
                    color: 'white',
                    customClass:
                      'fans-group-add-button',

                    action: () => {
                      modalData.addModal(
                        t('addUsers'),
                        <AddUsers
                          dependecyArray={
                            dependecyArray as any
                          }
                          customSubmit={(
                            payload: {
                              ids: string[]
                              users: any[]
                            }
                          ) =>
                            addFansToGroup.mutate(
                              payload
                            )
                          }
                          groupId={groupId}
                        />,
                        false,
                        true
                      )
                    }
                  }
                ])
          ]}
        />
      }
    >
      <div className='fans-custom-group-title-container'>
        <h1 className='fansCustomGroup--h1'>
          {groupTitle}
        </h1>

        {!isLoading && (
          <div className='fans-custom-group-count'>
            <span>{users.length}</span> {t('Fans')}
          </div>
        )}
      </div>

      {filterOption !== '' && (
        <div className='fans-custom-group-active-filter'>
          <div
            className='fans-custom-group-active-filter-close'
            onClick={() =>
              setFilterOption('')
            }
          >
            <Icons.IconClose />
          </div>

          {
            filterItemsBy.filter(
              (item: any) =>
                item.value === filterOption
            )[0].name
          }

          {filterSelectDefaults[filterOption] && (
            <span>
              {
                filterItemsBy
                  .filter(
                    (item: any) =>
                      item.value === filterOption
                  )[0]
                  .selectOptions?.filter(
                    (item: any) =>
                      item.value ===
                      filterSelectDefaults[
                        filterOption
                      ]
                  )[0]
                  .label
              }
            </span>
          )}
        </div>
      )}

      {isLoading && (
        <div className='fans-home-loader-container'>
          <div className='loader'></div>
        </div>
      )}

      {!isLoading &&
        users
          .slice()
          .map((user: any) => {
            const convId =
              typeof user?.conversation_id ===
                'string' &&
              user.conversation_id.length > 0
                ? user.conversation_id
                : null

            const isSubscribed =
              Boolean(user?.isSubscribed)

            return (
              <ActionCard
                key={user.id}
                text={user.display_name || ''}
                subtext={
                  user.username
                    ? `@${user.username}`
                    : ''
                }
                description={
                  renderSortOptionTags(user)
                }
                avatar={
                  <img
                    onClick={() =>
                      modalData.addModal(
                        '',
                        <UserDetails
                          user={user}
                        />,
                        true
                      )
                    }
                    className='addUsers__modal__avatar'
                    src={user.avatar?.url}
                    alt={t('avatar')}
                  />
                }
                customHtml={
                  <div className='actionCard__fans__icons'>
                    <div
                      className='actionCard__fans__icons--twoPeople'
                      onClick={() => {
                        modalData.addModal(
                          t('addUserToGroups'),
                          <AddUserToGroups
                            applyFn={(
                              val: any
                            ) => {
                              addFanToGroupsMutation.mutate(
                                {
                                  id: user.id,
                                  groupIds: val
                                }
                              )
                            }}
                            id={user.id}
                          />,
                          false,
                          true
                        )
                      }}
                    >
                      <Icons.IconTwoPeopleOutline />
                    </div>

                    <Link
                      to={`/chat/${user.id}`}
                      state={{
                        otherUserData: {
                          ...user,
                          isSubscribed
                        },
                        ...(convId
                          ? {
                              conversationId:
                                convId
                            }
                          : {})
                      }}
                      className='actionCard__fans__icons--chatOutline'
                    >
                      <Icons.IconChatOutline
                        color='#2894FF'
                      />
                    </Link>
                  </div>
                }
              />
            )
          })}

      {!isLoading && !groupDefault && (
        <ActionCard
          text={t('addUsers')}
          addNewText
          icon={
            <Icons.IconPlusInBox
              width='22'
              height='22'
            />
          }
          iconSize='small'
          clickFn={() => {
            modalData.addModal(
              t('addUsers'),
              <AddUsers
                dependecyArray={
                  dependecyArray as any
                }
                customSubmit={(
                  payload: {
                    ids: string[]
                    users: any[]
                  }
                ) =>
                  addFansToGroup.mutate(
                    payload
                  )
                }
                groupId={groupId}
              />,
              false,
              true
            )
          }}
        />
      )}
    </WithHeaderSection>
  )
}